import { confirmInApp, promptInApp } from "../common/AppDialog";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DndContext, DragOverlay, closestCenter, useDraggable, useDroppable } from "@dnd-kit/core";
import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  Briefcase,
  ChevronDown,
  ChevronRight,
  ClipboardCopy,
  Eraser,
  FileText,
  FolderOpen,
  GripVertical,
  Layers3,
  PanelLeft,
  Plus,
  X,
} from "lucide-react";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useEditLock } from "../../context/EditLockContext";
import { useMobile } from "../../hooks/useMobile";
import { useDeskDnDSensors } from "../../hooks/useDeskDnDSensors";
import {
  createFolderNote,
  getChildren,
  getTopLevelTexts,
  moveText,
  moveTextsBatch,
  moveTextUnderParent,
  reorderTexts,
  setTextKind,
  toSortedArray,
  updateText,
} from "../../services/workspaceService";
import { titleFromPaste } from "../../utils/normalizePaste";
import { rememberWritingText } from "../../utils/writingSession";
import { textReaderPath } from "../../utils/textRoutes";

function previewText(value) {
  return String(value || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/[`*_>#\[\]()]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 104);
}

function combinedText(text) {
  const title = String(text?.title || "").trim();
  const body = String(text?.content || "");
  if (title && body) return `${title}\n${body}`;
  return title || body;
}

function setReactTextareaValue(textarea, value) {
  if (!textarea) return;
  const setter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, "value")?.set;
  if (setter) setter.call(textarea, value);
  else textarea.value = value;
  textarea.dispatchEvent(new Event("input", { bubbles: true }));
  window.setTimeout(() => {
    try {
      textarea.focus();
      const end = value.length;
      textarea.setSelectionRange(end, end);
    } catch (_e) {}
  }, 0);
}

function cleanupDirtyText(value) {
  const raw = String(value || "")
    .replace(/\r\n?/g, "\n")
    .replace(/\u00a0/g, " ")
    .replace(/[\u200B-\u200D\uFEFF]/g, "");

  let inFence = false;
  const lines = raw.split("\n").map((original) => {
    const fence = /^\s*```/.test(original);
    if (fence) {
      inFence = !inFence;
      return original.replace(/[ \t]+$/g, "");
    }
    if (inFence) return original.replace(/[ \t]+$/g, "");

    let line = original.replace(/[ \t]+$/g, "");
    if (!line.trim()) return "";

    // Preserve markdown tables and HTML-heavy lines; only normalize prose.
    if (/^\s*\|.*\|\s*$/.test(line) || /<[^>]+>/.test(line)) return line;

    const leading = line.match(/^\s*/)?.[0] || "";
    const body = line.slice(leading.length)
      .replace(/[ \t]{2,}/g, " ")
      .replace(/\s+([,.;:!?])/g, "$1")
      .replace(/([,.;:!?])(?=[A-Za-zÁÉÍÓÚÜÑáéíóúüñ])/g, "$1 ");
    return leading + body;
  });

  return lines.join("\n").replace(/\n{4,}/g, "\n\n\n").trim();
}

function enrichText(text, desk, drawer, notebook) {
  return {
    ...text,
    deskId: desk.id,
    deskName: desk.name,
    deskSlug: desk.slug,
    drawerId: drawer.id,
    drawerName: drawer.name,
    drawerSlug: drawer.slug,
    notebookId: notebook.id,
    notebookName: notebook.name,
    notebookSlug: notebook.slug,
    notebookColor: notebook.color,
  };
}

function sameNotebook(a, b) {
  return a?.deskId === b?.deskId && a?.drawerId === b?.drawerId && a?.notebookId === b?.notebookId;
}

function collectSubtreeIds(texts, rootId) {
  const out = [];
  const visit = (id) => {
    if (out.includes(id)) return;
    out.push(id);
    texts.filter((t) => t.parentId === id).forEach((child) => visit(child.id));
  };
  visit(rootId);
  return out;
}

function BeforeDrop({ target, depth }) {
  const id = `before:${target.deskId}:${target.drawerId}:${target.notebookId}:${target.id}`;
  const { setNodeRef, isOver } = useDroppable({
    id,
    data: { type: "before", target },
  });
  return (
    <div
      ref={setNodeRef}
      aria-hidden="true"
      style={{
        height: 10,
        marginLeft: 10 + depth * 14,
        marginRight: 8,
        borderTop: isOver ? "3px solid #D4AF37" : "3px solid transparent",
        transition: "border-color 100ms ease",
      }}
    />
  );
}

function RootDrop({ desk, drawer, notebook }) {
  const path = {
    deskId: desk.id,
    drawerId: drawer.id,
    notebookId: notebook.id,
    deskName: desk.name,
    drawerName: drawer.name,
    notebookName: notebook.name,
  };
  const id = `root:${desk.id}:${drawer.id}:${notebook.id}`;
  const { setNodeRef, isOver } = useDroppable({ id, data: { type: "root", path } });
  return (
    <div
      ref={setNodeRef}
      style={{
        margin: "4px 8px 8px 30px",
        minHeight: 34,
        borderRadius: 8,
        border: `1px dashed ${isOver ? "rgba(212,175,55,0.9)" : "rgba(255,255,255,0.12)"}`,
        background: isOver ? "rgba(212,175,55,0.12)" : "rgba(255,255,255,0.02)",
        display: "flex",
        alignItems: "center",
        padding: "0 10px",
        fontSize: 11,
        color: isOver ? "#D4AF37" : "rgba(255,255,255,0.45)",
      }}
    >
      Soltar aquí = raíz de {notebook.name}
    </div>
  );
}

function TextRow({ text, depth, active, folderOpen, onToggleFolder, onOpen, onToggleKind }) {
  const dragId = `drag:${text.deskId}:${text.drawerId}:${text.notebookId}:${text.id}`;
  const dropId = `inside:${text.deskId}:${text.drawerId}:${text.notebookId}:${text.id}`;
  const draggable = useDraggable({ id: dragId, data: { type: "text", source: text } });
  const droppable = useDroppable({ id: dropId, data: { type: "text", target: text } });
  const setNodeRef = useCallback((node) => {
    draggable.setNodeRef(node);
    droppable.setNodeRef(node);
  }, [draggable, droppable]);
  const isFolder = text.kind === "folder";

  return (
    <div
      ref={setNodeRef}
      style={{
        display: "flex",
        alignItems: "stretch",
        minHeight: 52,
        margin: "0 6px",
        paddingLeft: depth * 14,
        borderRadius: 9,
        background: droppable.isOver
          ? "rgba(212,175,55,0.2)"
          : active
            ? "rgba(212,175,55,0.12)"
            : "transparent",
        opacity: draggable.isDragging ? 0.45 : 1,
        outline: droppable.isOver ? "1px solid rgba(212,175,55,0.65)" : "none",
      }}
    >
      <button
        type="button"
        {...draggable.attributes}
        {...draggable.listeners}
        aria-label={`Arrastrar ${text.title || "archivo"}`}
        title="Arrastrar para mover o anidar"
        style={styles.grip}
      >
        <GripVertical size={17} />
      </button>

      {isFolder ? (
        <button type="button" style={styles.chevronButton} onClick={onToggleFolder}>
          {folderOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </button>
      ) : (
        <span style={styles.chevronSpacer} />
      )}

      <button type="button" style={styles.textButton} onClick={onOpen}>
        <span style={styles.textIcon}>{isFolder ? <FolderOpen size={17} /> : <FileText size={17} />}</span>
        <span style={styles.textCopy}>
          <span style={styles.textTitle}>{text.title || "Sin título"}</span>
          {previewText(text.content) && <span style={styles.snippet}>{previewText(text.content)}</span>}
        </span>
      </button>

      <button
        type="button"
        style={styles.kindButton}
        onClick={onToggleKind}
        title={isFolder ? "Convertir carpeta en nota" : "Convertir nota en carpeta"}
        aria-label={isFolder ? "Convertir carpeta en nota" : "Convertir nota en carpeta"}
      >
        {isFolder ? <FileText size={16} /> : <FolderOpen size={16} />}
      </button>
    </div>
  );
}

export default function LongformProjectTools() {
  const { workspace } = useWorkspace();
  const { canEdit } = useEditLock();
  const locked = !canEdit;
  const isMobile = useMobile();
  const sensors = useDeskDnDSensors(locked);
  const navigate = useNavigate();

  const [modalPresent, setModalPresent] = useState(false);
  const [headerLeft, setHeaderLeft] = useState(null);
  const [headerActions, setHeaderActions] = useState(null);
  const [toolbarLeft, setToolbarLeft] = useState(null);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [status, setStatus] = useState("");
  const [activeDrag, setActiveDrag] = useState(null);
  const statusTimerRef = useRef(null);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const locate = () => {
      const modal = document.querySelector(".text-editor-modal");
      const left = modal?.querySelector(".text-editor-header-left") || null;
      const actions = modal?.querySelector(".text-editor-header-actions") || null;
      const toolbar = modal?.querySelector(".text-editor-toolbar-left") || null;
      setModalPresent((prev) => (prev === Boolean(modal) ? prev : Boolean(modal)));
      setHeaderLeft((prev) => (prev === left ? prev : left));
      setHeaderActions((prev) => (prev === actions ? prev : actions));
      setToolbarLeft((prev) => (prev === toolbar ? prev : toolbar));
      if (!modal) {
        setOpen(false);
        setSelectedTarget(null);
      }
    };
    locate();
    const observer = new MutationObserver(locate);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => () => {
    if (statusTimerRef.current) window.clearTimeout(statusTimerRef.current);
  }, []);

  const desks = useMemo(() => toSortedArray(workspace?.desks), [workspace]);
  const flash = useCallback((message) => {
    setStatus(message);
    if (statusTimerRef.current) window.clearTimeout(statusTimerRef.current);
    statusTimerRef.current = window.setTimeout(() => setStatus(""), 2200);
  }, []);

  const toggle = useCallback((key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] })), []);

  const expandFirstPath = useCallback(() => {
    const desk = desks[0];
    const drawer = toSortedArray(desk?.drawers)[0];
    const notebook = toSortedArray(drawer?.notebooks)[0];
    setExpanded((prev) => ({
      ...prev,
      ...(desk ? { [`desk-${desk.id}`]: true } : {}),
      ...(drawer ? { [`drawer-${drawer.id}`]: true } : {}),
      ...(notebook ? { [`notebook-${notebook.id}`]: true } : {}),
    }));
  }, [desks]);

  const openText = useCallback(async (text) => {
    const textarea = document.querySelector(".text-editor-modal .text-editor-textarea");
    if (!textarea) return;
    if (selectedTarget?.id === text.id) {
      setOpen(false);
      return;
    }

    const current = String(textarea.value || "");
    const baseline = selectedTarget ? combinedText(selectedTarget) : "";
    const hasUnsaved = selectedTarget ? current !== baseline : Boolean(current.trim());
    if (hasUnsaved && !(await confirmInApp({
      title: "Cambiar de archivo",
      message: "¿Cambiar de archivo sin guardar este texto? El borrador local se conserva.",
      confirmLabel: "Cambiar",
      danger: true,
    }))) return;

    setReactTextareaValue(textarea, combinedText(text));
    setSelectedTarget(text);
    rememberWritingText(text);
    navigate(textReaderPath(text), { replace: true, state: { editorOpen: true } });
    setOpen(false);
    flash(`Abierto · ${text.title || "Sin título"}`);
  }, [flash, navigate, selectedTarget]);

  const saveSelected = useCallback(async () => {
    if (!selectedTarget || locked) return;
    const textarea = document.querySelector(".text-editor-modal .text-editor-textarea");
    if (!textarea) return;
    const raw = String(textarea.value || "");
    const lines = raw.split("\n");
    let title = String(lines[0] || "").trim();
    let body = lines.slice(1).join("\n");
    if (!body.trim() && raw.trim()) {
      title = title || titleFromPaste(raw);
      body = raw.trim();
    }
    if (!title) title = titleFromPaste(body || raw) || "Sin título";
    if (!body.trim()) {
      flash("Agregá cuerpo antes de guardar");
      return;
    }

    try {
      await updateText(selectedTarget.deskId, selectedTarget.drawerId, selectedTarget.notebookId, selectedTarget.id, {
        title,
        content: body,
      });
      const next = { ...selectedTarget, title, content: body };
      setSelectedTarget(next);
      rememberWritingText(next);
      flash("Guardado ✓");
    } catch (error) {
      console.error("[LongformProjectTools] save failed:", error);
      flash("Error al guardar");
    }
  }, [flash, locked, selectedTarget]);

  useEffect(() => {
    if (!modalPresent || !selectedTarget) return undefined;
    const interceptSaveClick = (event) => {
      const button = event.target?.closest?.("button");
      if (!button || !button.closest?.(".text-editor-modal")) return;
      if (button.dataset.longformTool === "1") return;
      const label = String(button.textContent || "").toLowerCase();
      const title = String(button.getAttribute?.("title") || "").toLowerCase();
      if (!label.includes("guardar") && !title.includes("guardar")) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      saveSelected();
    };
    const interceptSaveKey = (event) => {
      if (!(event.ctrlKey || event.metaKey) || event.key !== "Enter") return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      saveSelected();
    };
    document.addEventListener("click", interceptSaveClick, true);
    document.addEventListener("keydown", interceptSaveKey, true);
    return () => {
      document.removeEventListener("click", interceptSaveClick, true);
      document.removeEventListener("keydown", interceptSaveKey, true);
    };
  }, [modalPresent, saveSelected, selectedTarget]);

  const cleanupEditor = useCallback(() => {
    const textarea = document.querySelector(".text-editor-modal .text-editor-textarea");
    if (!textarea) return;
    const before = String(textarea.value || "");
    const after = cleanupDirtyText(before);
    if (!after || after === before) {
      flash("Texto ya limpio");
      return;
    }
    setReactTextareaValue(textarea, after);
    flash("Limpieza aplicada · revisá y guardá");
  }, [flash]);

  useEffect(() => {
    if (!modalPresent) return undefined;
    const onKey = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key.toLowerCase() === "l") {
        event.preventDefault();
        cleanupEditor();
      }
      if (open && event.key === "Escape") {
        event.stopPropagation();
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [cleanupEditor, modalPresent, open]);

  const copyPermalink = useCallback(async () => {
    if (!selectedTarget) return;
    const url = new URL(textReaderPath(selectedTarget), window.location.origin).href;
    try {
      await navigator.clipboard.writeText(url);
      flash("Link copiado ✓");
    } catch (_e) {
      await promptInApp({
        title: "Copiar link",
        message: "El navegador no permitió copiar automáticamente. Copiá el enlace desde acá.",
        defaultValue: url,
        confirmLabel: "Listo",
      });
    }
  }, [flash, selectedTarget]);

  const locateNotebook = useCallback((path) => {
    const desk = desks.find((d) => d.id === path.deskId);
    const drawer = toSortedArray(desk?.drawers).find((d) => d.id === path.drawerId);
    const notebook = toSortedArray(drawer?.notebooks).find((n) => n.id === path.notebookId);
    return { desk, drawer, notebook };
  }, [desks]);

  const moveSourceTo = useCallback(async (source, destination, parentId) => {
    const same = sameNotebook(source, destination);
    if (same) {
      await moveTextUnderParent(source.deskId, source.drawerId, source.notebookId, source.id, parentId || null);
    } else {
      const { notebook: sourceNotebook } = locateNotebook(source);
      const sourceTexts = toSortedArray(sourceNotebook?.texts);
      const subtreeIds = collectSubtreeIds(sourceTexts, source.id);
      if (subtreeIds.length > 1) {
        await moveTextsBatch(
          source.deskId,
          source.drawerId,
          source.notebookId,
          subtreeIds,
          destination.deskId,
          destination.drawerId,
          destination.notebookId,
        );
      } else {
        await moveText(
          source.deskId,
          source.drawerId,
          source.notebookId,
          source.id,
          destination.deskId,
          destination.drawerId,
          destination.notebookId,
        );
      }
      await moveTextUnderParent(destination.deskId, destination.drawerId, destination.notebookId, source.id, parentId || null);
    }

    if (selectedTarget?.id === source.id) {
      const next = {
        ...selectedTarget,
        deskId: destination.deskId,
        drawerId: destination.drawerId,
        notebookId: destination.notebookId,
        deskName: destination.deskName || selectedTarget.deskName,
        drawerName: destination.drawerName || selectedTarget.drawerName,
        notebookName: destination.notebookName || selectedTarget.notebookName,
        parentId: parentId || undefined,
      };
      setSelectedTarget(next);
      rememberWritingText(next);
    }
  }, [locateNotebook, selectedTarget]);

  const handleDragEnd = useCallback(async ({ active, over }) => {
    const source = active?.data?.current?.source;
    const targetData = over?.data?.current;
    setActiveDrag(null);
    if (!source || !targetData || locked) return;

    try {
      if (targetData.type === "text") {
        const target = targetData.target;
        if (!target || target.id === source.id) return;
        if (sameNotebook(source, target)) {
          const { notebook } = locateNotebook(source);
          const subtree = collectSubtreeIds(toSortedArray(notebook?.texts), source.id);
          if (subtree.includes(target.id)) {
            flash("No podés mover una carpeta dentro de su descendiente");
            return;
          }
        }
        if (target.kind !== "folder") {
          await setTextKind(target.deskId, target.drawerId, target.notebookId, target.id, "folder");
          if (selectedTarget?.id === target.id) setSelectedTarget((prev) => ({ ...prev, kind: "folder" }));
        }
        await moveSourceTo(source, target, target.id);
        setExpanded((prev) => ({ ...prev, [`folder-${target.id}`]: true }));
        flash(`Movido dentro de ${target.title || "carpeta"}`);
        return;
      }

      if (targetData.type === "root") {
        await moveSourceTo(source, targetData.path, null);
        flash(`Movido a ${targetData.path.notebookName || "raíz"}`);
        return;
      }

      if (targetData.type === "before") {
        const target = targetData.target;
        if (!target || target.id === source.id) return;
        const parentId = target.parentId || null;
        await moveSourceTo(source, target, parentId);
        const { notebook } = locateNotebook(target);
        const siblingIds = toSortedArray(notebook?.texts)
          .filter((t) => (t.parentId || null) === parentId && t.id !== source.id)
          .map((t) => t.id);
        const targetIndex = Math.max(0, siblingIds.indexOf(target.id));
        siblingIds.splice(targetIndex, 0, source.id);
        await reorderTexts(target.deskId, target.drawerId, target.notebookId, siblingIds);
        flash(`Reordenado antes de ${target.title || "archivo"}`);
      }
    } catch (error) {
      console.error("[LongformProjectTools] drag failed:", error);
      flash(error?.message || "No se pudo mover");
    }
  }, [flash, locateNotebook, locked, moveSourceTo, selectedTarget]);

  const toggleKind = useCallback(async (text) => {
    if (locked) return;
    const nextKind = text.kind === "folder" ? "note" : "folder";
    try {
      await setTextKind(text.deskId, text.drawerId, text.notebookId, text.id, nextKind);
      if (selectedTarget?.id === text.id) setSelectedTarget((prev) => ({ ...prev, kind: nextKind }));
      flash(nextKind === "folder" ? "Convertido en carpeta" : "Convertido en nota");
    } catch (error) {
      flash(error?.message || "No se pudo convertir");
    }
  }, [flash, locked, selectedTarget]);

  const createFolder = useCallback(async (desk, drawer, notebook) => {
    if (locked) return;
    const title = await promptInApp({
      title: "Nombre de la carpeta",
      confirmLabel: "Crear",
    });
    if (!title?.trim()) return;
    try {
      await createFolderNote(desk.id, drawer.id, notebook.id, title.trim());
      setExpanded((prev) => ({ ...prev, [`notebook-${notebook.id}`]: true }));
      flash("Carpeta creada");
    } catch (error) {
      flash(error?.message || "No se pudo crear la carpeta");
    }
  }, [flash, locked]);

  const renderTextTree = useCallback((text, desk, drawer, notebook, allTexts, depth = 0) => {
    const enriched = enrichText(text, desk, drawer, notebook);
    const isFolder = text.kind === "folder";
    const folderKey = `folder-${text.id}`;
    const folderOpen = Boolean(expanded[folderKey]);
    const kids = isFolder ? getChildren(allTexts, text.id) : [];
    return (
      <React.Fragment key={text.id}>
        <BeforeDrop target={enriched} depth={depth} />
        <TextRow
          text={enriched}
          depth={depth}
          active={selectedTarget?.id === text.id}
          folderOpen={folderOpen}
          onToggleFolder={() => toggle(folderKey)}
          onOpen={() => openText(enriched)}
          onToggleKind={() => toggleKind(enriched)}
        />
        {isFolder && folderOpen && kids.map((child) => renderTextTree(child, desk, drawer, notebook, allTexts, depth + 1))}
      </React.Fragment>
    );
  }, [expanded, openText, selectedTarget, toggle, toggleKind]);

  if (!modalPresent || typeof document === "undefined") return null;

  const launcher = headerLeft ? createPortal(
    <button
      type="button"
      data-longform-tool="1"
      onClick={() => {
        setOpen((v) => !v);
        if (!open) window.setTimeout(expandFirstPath, 0);
      }}
      style={styles.launcher}
      title="Árbol del proyecto"
      aria-label="Abrir árbol del proyecto"
    >
      <PanelLeft size={20} />
      {!isMobile && <span>Archivos</span>}
    </button>,
    headerLeft,
  ) : null;

  const cleanupButton = toolbarLeft ? createPortal(
    <button
      type="button"
      data-longform-tool="1"
      onClick={cleanupEditor}
      className="text-editor-toolbar-btn"
      title="Limpiar texto sucio (Ctrl+Shift+L)"
    >
      <Eraser size={isMobile ? 14 : 16} />
      {!isMobile && <span>Limpiar</span>}
    </button>,
    toolbarLeft,
  ) : null;

  const linkButton = headerActions && selectedTarget ? createPortal(
    <button
      type="button"
      data-longform-tool="1"
      onClick={copyPermalink}
      className="text-editor-header-btn"
      title="Copiar link estable de este artículo"
    >
      <ClipboardCopy size={isMobile ? 16 : 18} />
      {!isMobile && <span>Link</span>}
    </button>,
    headerActions,
  ) : null;

  const panel = open ? createPortal(
    <>
      <div style={styles.backdrop} onClick={() => setOpen(false)} />
      <aside style={{ ...styles.panel, width: isMobile ? "min(92vw, 430px)" : 390 }} aria-label="Árbol del proyecto">
        <div style={styles.panelHeader}>
          <div style={{ minWidth: 0 }}>
            <div style={styles.eyebrow}>PROYECTO</div>
            <div style={styles.activeTitle}>{selectedTarget?.title || "Archivos y carpetas"}</div>
            <div style={styles.help}>Arrastrá al archivo = meter dentro · línea dorada = reordenar · raíz = sacar de carpeta.</div>
          </div>
          <button type="button" style={styles.closeButton} onClick={() => setOpen(false)} aria-label="Cerrar árbol"><X size={21} /></button>
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={({ active }) => setActiveDrag(active?.data?.current?.source || null)}
          onDragCancel={() => setActiveDrag(null)}
          onDragEnd={handleDragEnd}
        >
          <div style={styles.scrollArea}>
            {desks.map((desk) => {
              const deskKey = `desk-${desk.id}`;
              const deskOpen = Boolean(expanded[deskKey]);
              return (
                <div key={desk.id}>
                  <button type="button" style={styles.groupRow} onClick={() => toggle(deskKey)}>
                    {deskOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    <Briefcase size={17} />
                    <span style={styles.groupTitle}>{desk.name}</span>
                  </button>
                  {deskOpen && toSortedArray(desk.drawers).map((drawer) => {
                    const drawerKey = `drawer-${drawer.id}`;
                    const drawerOpen = Boolean(expanded[drawerKey]);
                    return (
                      <div key={drawer.id} style={{ marginLeft: 10 }}>
                        <button type="button" style={styles.groupRow} onClick={() => toggle(drawerKey)}>
                          {drawerOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                          <Layers3 size={17} />
                          <span style={styles.groupTitle}>{drawer.name}</span>
                        </button>
                        {drawerOpen && toSortedArray(drawer.notebooks).map((notebook) => {
                          const notebookKey = `notebook-${notebook.id}`;
                          const notebookOpen = Boolean(expanded[notebookKey]);
                          const allTexts = toSortedArray(notebook.texts);
                          const roots = getTopLevelTexts(allTexts);
                          return (
                            <div key={notebook.id} style={{ marginLeft: 10 }}>
                              <div style={styles.notebookRow}>
                                <button type="button" style={{ ...styles.groupRow, flex: 1 }} onClick={() => toggle(notebookKey)}>
                                  {notebookOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                  <BookOpen size={17} />
                                  <span style={styles.groupTitle}>{notebook.name}</span>
                                </button>
                                {!locked && (
                                  <button type="button" style={styles.addFolderButton} onClick={() => createFolder(desk, drawer, notebook)} title="Nueva carpeta">
                                    <Plus size={17} />
                                  </button>
                                )}
                              </div>
                              {notebookOpen && (
                                <div>
                                  <RootDrop desk={desk} drawer={drawer} notebook={notebook} />
                                  {roots.length ? roots.map((text) => renderTextTree(text, desk, drawer, notebook, allTexts, 0)) : (
                                    <div style={styles.empty}>Sin textos</div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {!desks.length && <div style={styles.empty}>No hay proyecto cargado.</div>}
          </div>
          <DragOverlay>
            {activeDrag ? <div style={styles.dragOverlay}><GripVertical size={16} /> {activeDrag.title || "Sin título"}</div> : null}
          </DragOverlay>
        </DndContext>
      </aside>
    </>,
    document.body,
  ) : null;

  const toast = status ? createPortal(<div style={styles.toast}>{status}</div>, document.body) : null;

  return <>{launcher}{cleanupButton}{linkButton}{panel}{toast}</>;
}

const styles = {
  launcher: {
    minWidth: 44,
    minHeight: 44,
    padding: "0 10px",
    borderRadius: 10,
    border: "1px solid rgba(212,175,55,0.3)",
    background: "rgba(212,175,55,0.08)",
    color: "#D4AF37",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    cursor: "pointer",
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 700,
  },
  backdrop: { position: "fixed", inset: 0, zIndex: 14000, background: "rgba(0,0,0,0.44)" },
  panel: {
    position: "fixed",
    zIndex: 14001,
    top: 0,
    left: 0,
    bottom: 0,
    maxWidth: "94vw",
    background: "rgba(18,18,18,0.985)",
    borderRight: "1px solid rgba(212,175,55,0.22)",
    boxShadow: "18px 0 48px rgba(0,0,0,0.48)",
    display: "flex",
    flexDirection: "column",
    paddingTop: "env(safe-area-inset-top)",
    paddingBottom: "env(safe-area-inset-bottom)",
  },
  panelHeader: {
    minHeight: 92,
    padding: "14px 12px 12px 16px",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
  },
  eyebrow: { fontSize: 10, letterSpacing: "0.14em", color: "#D4AF37", fontWeight: 800 },
  activeTitle: { color: "#fff", fontWeight: 750, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  help: { color: "rgba(255,255,255,0.46)", fontSize: 10.5, lineHeight: 1.3, marginTop: 5 },
  closeButton: { width: 42, height: 42, borderRadius: 10, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)", color: "#bbb", cursor: "pointer", flexShrink: 0 },
  scrollArea: { flex: 1, overflowY: "auto", overscrollBehavior: "contain", padding: "8px 5px 100px" },
  groupRow: { minHeight: 45, display: "flex", alignItems: "center", gap: 7, width: "100%", border: 0, background: "transparent", color: "rgba(255,255,255,0.78)", cursor: "pointer", textAlign: "left", padding: "0 8px", borderRadius: 8 },
  groupTitle: { flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13, fontWeight: 650 },
  notebookRow: { display: "flex", alignItems: "center" },
  addFolderButton: { width: 40, height: 40, marginRight: 5, borderRadius: 9, border: "1px solid rgba(212,175,55,0.18)", background: "rgba(212,175,55,0.06)", color: "#D4AF37", cursor: "pointer", display: "grid", placeItems: "center" },
  grip: { width: 36, minWidth: 36, border: 0, background: "transparent", color: "rgba(255,255,255,0.28)", cursor: "grab", touchAction: "none", display: "grid", placeItems: "center" },
  chevronButton: { width: 30, minWidth: 30, border: 0, background: "transparent", color: "rgba(255,255,255,0.5)", cursor: "pointer", display: "grid", placeItems: "center" },
  chevronSpacer: { width: 30, minWidth: 30 },
  textButton: { flex: 1, minWidth: 0, border: 0, background: "transparent", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, padding: "6px 4px", textAlign: "left" },
  textIcon: { width: 22, minWidth: 22, color: "#D4AF37", display: "grid", placeItems: "center" },
  textCopy: { minWidth: 0, display: "flex", flexDirection: "column", gap: 2 },
  textTitle: { fontSize: 13, fontWeight: 650, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" },
  snippet: { fontSize: 10.5, lineHeight: 1.25, color: "rgba(255,255,255,0.42)", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" },
  kindButton: { width: 40, minWidth: 40, border: 0, background: "transparent", color: "rgba(255,255,255,0.4)", cursor: "pointer", display: "grid", placeItems: "center" },
  empty: { padding: "14px 32px", color: "rgba(255,255,255,0.35)", fontSize: 12 },
  dragOverlay: { maxWidth: 300, padding: "10px 13px", borderRadius: 9, background: "rgba(28,28,28,0.98)", border: "1px solid rgba(212,175,55,0.45)", color: "#fff", boxShadow: "0 10px 28px rgba(0,0,0,0.45)", display: "flex", alignItems: "center", gap: 8, fontSize: 12 },
  toast: { position: "fixed", left: "50%", bottom: "calc(24px + env(safe-area-inset-bottom))", transform: "translateX(-50%)", zIndex: 16000, padding: "10px 14px", borderRadius: 10, background: "rgba(20,20,20,0.96)", border: "1px solid rgba(212,175,55,0.35)", color: "#F5E6A8", boxShadow: "0 8px 28px rgba(0,0,0,0.42)", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", maxWidth: "88vw", overflow: "hidden", textOverflow: "ellipsis" },
};
